import { Scene, MeshBuilder, StandardMaterial, Vector3, Mesh, VideoTexture } from '@babylonjs/core';
import { SUPABASE_STORAGE_ROOT, SUPABASE_VIDEOS_ROOT, SUPABASE_SOUNDS_ROOT, IMAGE_PLANE_POSITION, IMAGE_PLANE_SIZE, IMAGE_PLANE_CURVE, IMAGE_PLANE_SEGMENTS } from '../utils/constants';
import { MediaBundle } from '../supabase/storageSearch';

export interface ImagePlane {
  mesh: Mesh;
  playMedia(videoFile: string, audioFile: string): Promise<void>;
  playMediaBundle(bundle: MediaBundle): Promise<void>;
}

export function createImagePlane(scene: Scene, idleVideoPath: string): ImagePlane {
  const { width, height } = IMAGE_PLANE_SIZE;
  const segments = IMAGE_PLANE_SEGMENTS;
  const curveAmount = IMAGE_PLANE_CURVE;
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const pointsPerPath = segments + 1;

  const pathArray: Vector3[][] = [];

  for (let i = 0; i <= segments; i++) {
    const y = -halfHeight + (i / segments) * height;
    const path: Vector3[] = [];

    for (let j = 0; j < pointsPerPath; j++) {
      const x = -halfWidth + (j / (pointsPerPath - 1)) * width;
      const normalizedX = x / halfWidth;
      const z = curveAmount * (1 - normalizedX * normalizedX);
      path.push(new Vector3(x, y, z));
    }

    pathArray.push(path);
  }

  const ribbon = MeshBuilder.CreateRibbon('imageRibbon', {
    pathArray,
    sideOrientation: Mesh.DOUBLESIDE,
    closePath: false,
    closeArray: false,
  }, scene);

  ribbon.position = new Vector3(
    IMAGE_PLANE_POSITION.x,
    IMAGE_PLANE_POSITION.y,
    IMAGE_PLANE_POSITION.z
  );

  const material = new StandardMaterial('imageRibbonMaterial', scene);
  material.useAlphaFromDiffuseTexture = true;
  material.disableLighting = true;
  ribbon.material = material;

  let currentTexture: VideoTexture | null = null;
  let isPlaying = false;

  function applyIdleVideo(): void {
    if (currentTexture) {
      currentTexture.dispose();
      currentTexture = null;
    }

    const idleUrl = `${SUPABASE_STORAGE_ROOT}${idleVideoPath}`;
    const idleTexture = new VideoTexture('ribbonVideoIdle', idleUrl, scene, false, false, undefined, {
      autoPlay: true,
      loop: true,
      muted: true,
    });
    idleTexture.hasAlpha = true;

    material.diffuseTexture = idleTexture;
    material.emissiveTexture = idleTexture;
    currentTexture = idleTexture;
  }

  // Start with idle video
  applyIdleVideo();

  async function playMedia(videoFile: string, audioFile: string): Promise<void> {
    if (isPlaying) return;
    isPlaying = true;

    // Dispose idle texture
    if (currentTexture) {
      currentTexture.dispose();
      currentTexture = null;
    }

    const videoUrl = `${SUPABASE_VIDEOS_ROOT}${videoFile}`;
    const audioUrl = `${SUPABASE_SOUNDS_ROOT}${audioFile}`;

    // Create the one-shot video texture (muted - audio is separate)
    const videoTexture = new VideoTexture('ribbonVideoOneShot', videoUrl, scene, false, false, undefined, {
      autoPlay: false,
      loop: false,
      muted: true,
    });
    videoTexture.hasAlpha = true;

    material.diffuseTexture = videoTexture;
    material.emissiveTexture = videoTexture;
    currentTexture = videoTexture;

    // Create audio element
    const audio = new Audio(audioUrl);
    audio.preload = 'auto';

    // Wait for both media to be ready
    const videoReady = new Promise<void>((resolve, reject) => {
      const videoEl = videoTexture.video;
      if (videoEl.readyState >= 3) {
        resolve();
        return;
      }
      videoEl.addEventListener('canplay', () => resolve(), { once: true });
      videoEl.addEventListener('error', () => reject(new Error(`Video load failed: ${videoFile}`)), { once: true });
    });

    const audioReady = new Promise<void>((resolve, reject) => {
      if (audio.readyState >= 3) {
        resolve();
        return;
      }
      audio.addEventListener('canplay', () => resolve(), { once: true });
      audio.addEventListener('error', () => reject(new Error(`Audio load failed: ${audioFile}`)), { once: true });
    });

    try {
      await Promise.all([videoReady, audioReady]);

      // Play both simultaneously
      const videoEl = videoTexture.video;
      videoEl.play();
      audio.play();

      // Wait for playback to end
      await new Promise<void>((resolve) => {
        videoEl.addEventListener('ended', () => resolve(), { once: true });
      });
    } catch (err) {
      console.error('[ImagePlane] Media playback error:', err);
    } finally {
      // Cleanup one-shot media
      audio.pause();
      audio.src = '';
      isPlaying = false;

      // Restore idle loop
      applyIdleVideo();
    }
  }

  /**
   * Play media from a MediaBundle discovered via storage search.
   * Extracts video and audio files from the bundle and plays them.
   * Future: can also handle model loading, animations, etc.
   */
  async function playMediaBundle(bundle: MediaBundle): Promise<void> {
    if (!bundle.video || !bundle.audio) {
      console.error('[ImagePlane] MediaBundle missing required video or audio:',
        { video: bundle.video?.path, audio: bundle.audio?.path });
      return;
    }

    if (isPlaying) return;
    isPlaying = true;

    // Dispose idle texture
    if (currentTexture) {
      currentTexture.dispose();
      currentTexture = null;
    }

    const videoUrl = bundle.video.url;
    const audioUrl = bundle.audio.url;

    console.log('[ImagePlane] Playing media bundle:', {
      video: bundle.video.path,
      audio: bundle.audio.path,
      model: bundle.model?.path,
      totalFiles: bundle.files.length
    });

    // Create the one-shot video texture (muted - audio is separate)
    const videoTexture = new VideoTexture('ribbonVideoOneShot', videoUrl, scene, false, false, undefined, {
      autoPlay: false,
      loop: false,
      muted: true,
    });
    videoTexture.hasAlpha = true;

    material.diffuseTexture = videoTexture;
    material.emissiveTexture = videoTexture;
    currentTexture = videoTexture;

    // Create audio element
    const audio = new Audio(audioUrl);
    audio.preload = 'auto';

    // Wait for both media to be ready
    const videoReady = new Promise<void>((resolve, reject) => {
      const videoEl = videoTexture.video;
      if (videoEl.readyState >= 3) {
        resolve();
        return;
      }
      videoEl.addEventListener('canplay', () => resolve(), { once: true });
      videoEl.addEventListener('error', () => reject(new Error(`Video load failed: ${bundle.video!.path}`)), { once: true });
    });

    const audioReady = new Promise<void>((resolve, reject) => {
      if (audio.readyState >= 3) {
        resolve();
        return;
      }
      audio.addEventListener('canplay', () => resolve(), { once: true });
      audio.addEventListener('error', () => reject(new Error(`Audio load failed: ${bundle.audio!.path}`)), { once: true });
    });

    try {
      await Promise.all([videoReady, audioReady]);

      // Play both simultaneously
      const videoEl = videoTexture.video;
      videoEl.play();
      audio.play();

      // Wait for playback to end
      await new Promise<void>((resolve) => {
        videoEl.addEventListener('ended', () => resolve(), { once: true });
      });
    } catch (err) {
      console.error('[ImagePlane] Media bundle playback error:', err);
    } finally {
      // Cleanup one-shot media
      audio.pause();
      audio.src = '';
      isPlaying = false;

      // Restore idle loop
      applyIdleVideo();
    }
  }

  return { mesh: ribbon, playMedia, playMediaBundle };
}
